import type { CSSProperties } from 'react';
import type { NoteItem, RichTextItem, TableItem, TextAlign, TitleItem, TodoItem } from '../../doc/schema';
import { fontFamilyCss } from './fonts';

/** Block-level text styling shared by notes, titles and rich text blocks. */
export interface TextStyle {
  font: string | null;
  color: string | null;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  align: TextAlign;
}

export type TextItem = NoteItem | TitleItem | RichTextItem | TableItem | TodoItem;

/** Normalised style of an item; older items without the newer props get defaults. */
export function textStyleOf(item: TextItem): TextStyle {
  switch (item.type) {
    case 'note': {
      const p = item.props;
      return {
        font: p.font ?? null,
        color: p.textColor ?? null,
        fontSize: p.fontSize,
        bold: p.bold ?? false,
        italic: p.italic ?? false,
        align: p.align ?? 'left',
      };
    }
    case 'title': {
      const p = item.props;
      return {
        font: p.font ?? null,
        color: p.color ?? null,
        fontSize: p.fontSize,
        bold: p.bold,
        italic: p.italic ?? false,
        align: p.align,
      };
    }
    case 'richtext':
    case 'table':
    case 'todo': {
      const p = item.props;
      return {
        font: p.font ?? null,
        color: p.color ?? null,
        fontSize: p.fontSize,
        bold: false,
        italic: false,
        align: p.align ?? 'left',
      };
    }
  }
}

/** Maps a style patch onto the item type's prop names. */
export function textStylePatch(type: TextItem['type'], patch: Partial<TextStyle>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    if (k === 'color' && type === 'note') out.textColor = v;
    else out[k] = v;
  }
  return out;
}

export function textStyleCss(s: TextStyle, defaultColor?: string): CSSProperties {
  const css: CSSProperties = {
    fontSize: s.fontSize,
    fontWeight: s.bold ? 700 : 400,
    fontStyle: s.italic ? 'italic' : 'normal',
    textAlign: s.align,
  };
  const family = fontFamilyCss(s.font);
  if (family) css.fontFamily = family;
  const color = s.color ?? defaultColor;
  if (color) css.color = color;
  return css;
}

export const ALIGN_TO_JUSTIFY: Record<TextAlign, CSSProperties['justifyContent']> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};
